/**
 * Lo que el emisor contestó sobre un documento electrónico, literal.
 *
 * Es la frase que dice **qué corregir** — «Error en estructura del xml: El
 * elemento NumTelefono no es válido. El valor 89…» — frente a la nuestra, que
 * sólo dice que no timbró. Viene de `Importaciones_estadoFactura` en tres claves
 * que Business Central añadió para esto:
 *
 * ```json
 * { "mensajeElectronico": "…", "codigoElectronico": "…", "mensajeElectronicoCompleto": true }
 * ```
 *
 * Acuerdo `contrato-tipo-documento-electronico.md`, rondas 9 y 10.
 *
 * ## Por qué vive aparte y no dentro de `estadoDelTimbrado`
 *
 * Porque **no puede tocar la máquina de estados del timbrado**, y tenerlo en
 * otro fichero es lo que lo garantiza de un vistazo.
 *
 * El motivo es concreto: `noVaATimbrar` se deriva de que haya motivo, y
 * `esperarAlTimbre` corta la espera de la clave con ese estado. Si un texto del
 * emisor que dijera «en proceso» entrara por ahí, pintaría el documento en rojo
 * y dejaría tickets sin clave ni código de barras sobre ventas que iban a
 * timbrar perfectamente.
 *
 * Por eso este módulo **no importa `ESTADO_DEL_TIMBRADO` ni lo conoce**, y su
 * resultado no se guarda nunca en `invoices.Mensaje`, que es la ranura de
 * `motivoElectronico` y la entrada del distintivo rojo.
 *
 * ## Ausente no es lo mismo que vacío, y las dos veces se calla
 *
 * Las tres claves sólo existen desde la versión de la extensión que las publica;
 * antes **no vienen**. Y donde la migración del país no declara el campo del que
 * salen --Panamá, Ecuador-- vienen siempre vacías: el límite es la presencia del
 * campo, no el país.
 *
 * En los dos casos el resultado es el mismo: no hay nada que enseñar y no se
 * enseña nada. Vacío significa **«no se sabe»**, nunca «va bien»: un documento
 * aceptado también sale vacío, porque el emisor no manda mensaje cuando acepta.
 */

const texto = (valor) => String(valor ?? "").trim();

/**
 * Lee las tres claves de la respuesta.
 *
 * @param {object|null} respuesta Lo que devolvió `consultarEstadoFactura`.
 * @returns {{texto: string, codigo: string, completo: boolean}}
 */
export const leerLaRespuestaDelEmisor = (respuesta) => {
  if (!respuesta || typeof respuesta !== "object") {
    return { texto: "", codigo: "", completo: true };
  }

  return {
    texto: texto(respuesta.mensajeElectronico),
    codigo: texto(respuesta.codigoElectronico),
    // `completo` sólo es falso cuando Business Central dice que recortó. Ante la
    // duda se da por entero: poner puntos suspensivos sobre una frase completa
    // haría pensar que falta algo que sí está.
    completo: respuesta.mensajeElectronicoCompleto !== false,
  };
};

/**
 * Si hay algo que enseñarle al cajero.
 *
 * **Manda el texto, no el código.** Medido el 12/09/2026 sobre 31 facturas de
 * Costa Rica DEV con adjunto de respuesta: **29 traen `Codigo` y no traen
 * `Mensaje`** --el código de «sin error», con la frase vacía--. Si bastara el
 * código, la marca saldría en el 94% de los documentos, todos ellos correctos, y
 * con el tooltip en blanco: ruido en la única columna donde el cajero mira
 * cuando algo va mal.
 *
 * El código no se pierde: acompaña al texto cuando lo hay.
 *
 * @param {{texto: string, codigo: string}} respuesta
 */
export const hayRespuestaDelEmisor = (respuesta) => Boolean(texto(respuesta?.texto));

/**
 * El texto para un sitio estrecho, con puntos suspensivos sólo si de verdad falta.
 *
 * Business Central avisa él mismo de si recortó, así que no hay que adivinarlo:
 * los puntos suspensivos se ponen cuando `completo` es falso, o cuando el recorte
 * lo hacemos nosotros aquí.
 *
 * @param {{texto: string, completo: boolean}} respuesta
 * @param {number} largo
 */
export const respuestaRecortada = ({ texto: frase = "", completo = true } = {}, largo = 110) => {
  const limpia = texto(frase);
  if (!limpia) return "";
  if (limpia.length <= largo) return completo ? limpia : `${limpia}…`;
  return `${limpia.slice(0, largo).trimEnd()}…`;
};

/**
 * Lo que se guardó en la fila local de la última consulta.
 *
 * Vive aquí y no en un componente porque lo usan dos: el distintivo del listado
 * y el cajón de detalle. Y sigue sin importar nada: es leer cuatro propiedades.
 *
 * @param {object} factura
 * @returns {{texto: string, codigo: string, completo: boolean}}
 */
export const respuestaDeLaFila = (factura) => ({
  texto: texto(factura?.MensajeElectronico),
  codigo: texto(factura?.CodigoElectronico),
  completo: factura?.MensajeElectronicoCompleto !== false,
});
